import { ComponentType, DemoSemiFinishedProduct, Packaging, Product, RawMaterial, SemiFinishedProduct, TradingGood } from './types'

/**
 * Recursively computes the pure unit cost of a component item in the BOM tree.
 * Matches the backend calculation in calculate_component_tree_node.
 */
export function calculateRecursiveUnitCost(
  componentType: ComponentType,
  componentId: string,
  baseCost: number,
  productsMap: Map<string, Product>,
  semiFinishedMap: Map<string, SemiFinishedProduct>,
  rawMaterialsMap: Map<string, RawMaterial>,
  packagingsMap: Map<string, Packaging>,
  tradingGoodsMap?: Map<string, TradingGood>,
  visited: Set<string> = new Set()
): number {
  if (visited.has(componentId)) {
    return baseCost
  }

  if (componentType === 'raw_material') {
    const raw = rawMaterialsMap.get(componentId)
    return Number(raw?.maliyet ?? baseCost)
  }

  if (componentType === 'packaging') {
    const pkg = packagingsMap.get(componentId)
    return Number(pkg?.maliyet ?? baseCost)
  }

  if (componentType === 'trading_good') {
    const tg = tradingGoodsMap?.get(componentId)
    return Number(tg?.maliyet ?? baseCost)
  }

  const nextVisited = new Set(visited)
  nextVisited.add(componentId)

  let subCost = 0
  if (componentType === 'semi_finished') {
    const semi = semiFinishedMap.get(componentId)
    const subComps = semi?.components || []
    for (const child of subComps) {
      const childQty = Number(child.unit_amount || child.quantity || 0)
      const childBase = Number(child.birim_maliyet || 0)
      const childUnitCost = calculateRecursiveUnitCost(
        child.component_type,
        child.component_id,
        childBase,
        productsMap,
        semiFinishedMap,
        rawMaterialsMap,
        packagingsMap,
        tradingGoodsMap,
        nextVisited
      )
      subCost += childQty * childUnitCost
    }
    return subCost
  }

  if (componentType === 'product') {
    const prod = productsMap.get(componentId)
    const subComps = prod?.components || []
    for (const child of subComps) {
      const childQty = Number(child.unit_amount || child.quantity || 0)
      const childBase = Number(child.birim_maliyet || 0)
      const childUnitCost = calculateRecursiveUnitCost(
        child.component_type,
        child.component_id,
        childBase,
        productsMap,
        semiFinishedMap,
        rawMaterialsMap,
        packagingsMap,
        tradingGoodsMap,
        nextVisited
      )
      subCost += childQty * childUnitCost
    }
    return subCost
  }

  return baseCost
}

export function calculateProductTotalCost(
  product: Product,
  productsMap: Map<string, Product>,
  semiFinishedMap: Map<string, SemiFinishedProduct>,
  rawMaterialsMap: Map<string, RawMaterial>,
  packagingsMap: Map<string, Packaging>,
  tradingGoodsMap?: Map<string, TradingGood>
): number {
  return calculateRecursiveUnitCost(
    'product',
    product.id,
    0,
    productsMap,
    semiFinishedMap,
    rawMaterialsMap,
    packagingsMap,
    tradingGoodsMap,
    new Set()
  )
}

export function calculateSemiFinishedTotalCost(
  semiFinished: SemiFinishedProduct,
  productsMap: Map<string, Product>,
  semiFinishedMap: Map<string, SemiFinishedProduct>,
  rawMaterialsMap: Map<string, RawMaterial>,
  packagingsMap: Map<string, Packaging>,
  tradingGoodsMap?: Map<string, TradingGood>
): number {
  return calculateRecursiveUnitCost(
    'semi_finished',
    semiFinished.id,
    0,
    productsMap,
    semiFinishedMap,
    rawMaterialsMap,
    packagingsMap,
    tradingGoodsMap,
    new Set()
  )
}

export function calculateDemoSemiFinishedTotalCost(
  demoSemiFinished: any,
  demoSemiFinishedMap: Map<string, any> = new Map(),
  demoProductsMap: Map<string, any> = new Map(),
  productsMap: Map<string, Product> = new Map(),
  semiFinishedMap: Map<string, SemiFinishedProduct> = new Map(),
  rawMaterialsMap: Map<string, RawMaterial> = new Map(),
  packagingsMap: Map<string, Packaging> = new Map(),
  tradingGoodsMap: Map<string, TradingGood> = new Map(),
  visited: Set<string> = new Set()
): number {
  if (visited.has(demoSemiFinished.id)) {
    return Number(demoSemiFinished.maliyet || 0)
  }
  const nextVisited = new Set(visited)
  nextVisited.add(demoSemiFinished.id)

  const directBase = Number(demoSemiFinished.maliyet || 0)
  let subCost = 0
  const subComps = demoSemiFinished.components || []

  for (const child of subComps) {
    const childQty = Number(child.unit_amount || 0)
    let childUnitCost = 0
    const childId =
      child.component_demo_semi_finished_id ||
      child.component_demo_product_id ||
      child.component_product_id ||
      child.component_semi_finished_id ||
      child.component_raw_material_id ||
      child.component_packaging_id ||
      child.component_trading_good_id ||
      ''

    if (child.component_type === 'demo_semi_finished') {
      const dsf = demoSemiFinishedMap.get(childId)
      if (dsf) {
        childUnitCost = calculateDemoSemiFinishedTotalCost(
          dsf,
          demoSemiFinishedMap,
          demoProductsMap,
          productsMap,
          semiFinishedMap,
          rawMaterialsMap,
          packagingsMap,
          tradingGoodsMap,
          nextVisited
        )
      }
    } else if (child.component_type === 'demo_product') {
      const dp = demoProductsMap.get(childId)
      if (dp) {
        childUnitCost = calculateDemoProductTotalCost(
          dp,
          demoProductsMap,
          demoSemiFinishedMap,
          productsMap,
          semiFinishedMap,
          rawMaterialsMap,
          packagingsMap,
          tradingGoodsMap,
          nextVisited
        )
      }
    } else if (child.component_type === 'product') {
      childUnitCost = calculateRecursiveUnitCost('product', childId, 0, productsMap, semiFinishedMap, rawMaterialsMap, packagingsMap, tradingGoodsMap, nextVisited)
    } else if (child.component_type === 'semi_finished') {
      childUnitCost = calculateRecursiveUnitCost('semi_finished', childId, 0, productsMap, semiFinishedMap, rawMaterialsMap, packagingsMap, tradingGoodsMap, nextVisited)
    } else if (child.component_type === 'raw_material') {
      const rm = rawMaterialsMap.get(childId)
      childUnitCost = Number(rm?.maliyet || 0)
    } else if (child.component_type === 'packaging') {
      const pkg = packagingsMap.get(childId)
      childUnitCost = Number(pkg?.maliyet || 0)
    } else if (child.component_type === 'trading_good') {
      const tg = tradingGoodsMap.get(childId)
      childUnitCost = Number(tg?.maliyet || 0)
    }

    subCost += childQty * childUnitCost
  }

  return directBase + subCost
}

export function calculateDemoProductTotalCost(
  demoProduct: any,
  demoProductsMap: Map<string, any>,
  demoSemiFinishedMap: Map<string, any> = new Map(),
  productsMap: Map<string, Product> = new Map(),
  semiFinishedMap: Map<string, SemiFinishedProduct> = new Map(),
  rawMaterialsMap: Map<string, RawMaterial> = new Map(),
  packagingsMap: Map<string, Packaging> = new Map(),
  tradingGoodsMap: Map<string, TradingGood> = new Map(),
  visited: Set<string> = new Set()
): number {
  if (visited.has(demoProduct.id)) {
    return Number(demoProduct.maliyet || 0)
  }
  const nextVisited = new Set(visited)
  nextVisited.add(demoProduct.id)

  const directBase = Number(demoProduct.maliyet || 0)
  let subCost = 0
  const subComps = demoProduct.components || []

  for (const child of subComps) {
    const childQty = Number(child.unit_amount || 0)
    let childUnitCost = 0
    const childId =
      child.component_demo_product_id ||
      child.component_demo_semi_finished_id ||
      child.component_product_id ||
      child.component_semi_finished_id ||
      child.component_raw_material_id ||
      child.component_packaging_id ||
      child.component_trading_good_id ||
      ''

    if (child.component_type === 'demo_product') {
      const dp = demoProductsMap.get(childId)
      if (dp) {
        childUnitCost = calculateDemoProductTotalCost(
          dp,
          demoProductsMap,
          demoSemiFinishedMap,
          productsMap,
          semiFinishedMap,
          rawMaterialsMap,
          packagingsMap,
          tradingGoodsMap,
          nextVisited
        )
      }
    } else if (child.component_type === 'demo_semi_finished') {
      const dsf = demoSemiFinishedMap.get(childId)
      if (dsf) {
        childUnitCost = calculateDemoSemiFinishedTotalCost(
          dsf,
          demoSemiFinishedMap,
          demoProductsMap,
          productsMap,
          semiFinishedMap,
          rawMaterialsMap,
          packagingsMap,
          tradingGoodsMap,
          nextVisited
        )
      }
    } else if (child.component_type === 'product') {
      childUnitCost = calculateRecursiveUnitCost('product', childId, 0, productsMap, semiFinishedMap, rawMaterialsMap, packagingsMap, tradingGoodsMap, nextVisited)
    } else if (child.component_type === 'semi_finished') {
      childUnitCost = calculateRecursiveUnitCost('semi_finished', childId, 0, productsMap, semiFinishedMap, rawMaterialsMap, packagingsMap, tradingGoodsMap, nextVisited)
    } else if (child.component_type === 'raw_material') {
      const rm = rawMaterialsMap.get(childId)
      childUnitCost = Number(rm?.maliyet || 0)
    } else if (child.component_type === 'packaging') {
      const pkg = packagingsMap.get(childId)
      childUnitCost = Number(pkg?.maliyet || 0)
    } else if (child.component_type === 'trading_good') {
      const tg = tradingGoodsMap.get(childId)
      childUnitCost = Number(tg?.maliyet || 0)
    }

    subCost += childQty * childUnitCost
  }

  return directBase + subCost
}

/**
 * Calculates the total profit margin (Toplam Kar) between Genel Maliyet and KDV Hariç Satış Fiyatı.
 *
 * For Satış Fiyatı Üzerinden Kar Hesabı ('margin'):
 *   KDV Hariç Satış Fiyatı = Genel Maliyet / (1 - Toplam Kar)
 *   => Toplam Kar = (KDV Hariç Satış Fiyatı - Genel Maliyet) / KDV Hariç Satış Fiyatı
 *
 * For Maliyet Üzerinden Kar Hesabı ('cost_plus'):
 *   KDV Hariç Satış Fiyatı = Genel Maliyet * (1 + Toplam Kar)
 *   => Toplam Kar = (KDV Hariç Satış Fiyatı - Genel Maliyet) / Genel Maliyet
 */
export function calculateToplamKar(
  genelMaliyet: number,
  kdvHaricSatisFiyati: number,
  method: string = 'cost_plus'
): number | null {
  if (!isFinite(genelMaliyet) || !isFinite(kdvHaricSatisFiyati)) return null
  if (kdvHaricSatisFiyati === 0 && genelMaliyet === 0) return 0

  if (method === 'margin') {
    if (kdvHaricSatisFiyati === 0) return null
    return (kdvHaricSatisFiyati - genelMaliyet) / kdvHaricSatisFiyati
  } else {
    // Default: cost_plus
    if (genelMaliyet === 0) return null
    return (kdvHaricSatisFiyati - genelMaliyet) / genelMaliyet
  }
}

/**
 * Formats a profit margin ratio (e.g. 0.25 -> "+25.0%") matching other profit margin columns.
 */
export function formatProfitMargin(val: number | null | undefined): string {
  if (val === null || val === undefined || isNaN(val) || !isFinite(val)) return '-'
  const num = Math.abs(val * 100) < 0.00001 ? 0 : val * 100
  const pct = Math.abs(num).toFixed(1)
  return num >= 0 ? `+${pct}%` : `-${pct}%`
}

/**
 * Recursively calculates the total unit consumption (Birim Miktar) of targetRawMaterialId
 * required to produce 1 unit of the given item (Product or SemiFinishedProduct).
 * Accounts for both direct components and multi-level nested sub-components.
 */
export function calculateRawMaterialUnitConsumption(
  item: Product | SemiFinishedProduct,
  targetRawMaterialId: string,
  semiFinishedMap: Map<string, SemiFinishedProduct>,
  productsMap: Map<string, Product>,
  visited: Set<string> = new Set()
): number {
  if (visited.has(item.id)) return 0
  visited.add(item.id)

  let totalUnitAmount = 0
  const components = item.components || []

  for (const comp of components) {
    const compType = comp.component_type
    const compId =
      comp.component_id ||
      (comp as any).component_raw_material_id ||
      (comp as any).component_semi_finished_id ||
      (comp as any).component_product_id
    const unitQty = Number(comp.unit_amount || comp.quantity || 0)

    if (compType === 'raw_material' && compId === targetRawMaterialId) {
      totalUnitAmount += unitQty
    } else if (compType === 'semi_finished' && compId) {
      const childSf = semiFinishedMap.get(compId)
      if (childSf) {
        const subUsage = calculateRawMaterialUnitConsumption(
          childSf,
          targetRawMaterialId,
          semiFinishedMap,
          productsMap,
          new Set(visited)
        )
        totalUnitAmount += unitQty * subUsage
      }
    } else if (compType === 'product' && compId) {
      const childProd = productsMap.get(compId)
      if (childProd) {
        const subUsage = calculateRawMaterialUnitConsumption(
          childProd,
          targetRawMaterialId,
          semiFinishedMap,
          productsMap,
          new Set(visited)
        )
        totalUnitAmount += unitQty * subUsage
      }
    }
  }

  return totalUnitAmount
}

/**
 * Helper to check whether an item contains a raw material directly or indirectly.
 */
export function doesItemContainRawMaterial(
  item: Product | SemiFinishedProduct,
  targetRawMaterialId: string,
  semiFinishedMap: Map<string, SemiFinishedProduct>,
  productsMap: Map<string, Product>
): boolean {
  return calculateRawMaterialUnitConsumption(item, targetRawMaterialId, semiFinishedMap, productsMap) > 0.000001
}

/**
 * Checks whether a semi-finished product is eligible for recipe reports and pie chart buttons.
 * Criteria:
 * - Must strictly have unit 'Kg' (case-insensitive)
 * - All direct components must also strictly have unit 'Kg' (case-insensitive)
 */
export function isSemiFinishedEligibleForRecipeReport(
  semi: SemiFinishedProduct | DemoSemiFinishedProduct | null | undefined
): boolean {
  if (!semi) return false

  const semiUnit = (semi.unit || '').trim().toLowerCase()
  if (semiUnit !== 'kg') {
    return false
  }

  const components = semi.components || []
  const hasNonKgComponent = components.some((comp: any) => {
    const compUnit = (comp.component_unit || comp.unit || '').trim().toLowerCase()
    return compUnit !== 'kg'
  })

  return !hasNonKgComponent
}

/**
 * Formats a quantity/amount up to maxDecimals (default: 6), truncating trailing zeros.
 * Example: 1.060000 -> "1.06", 1.000000 -> "1", 0.000125 -> "0.000125"
 */
export function formatQuantity(
  val: number | string | null | undefined,
  maxDecimals: number = 6
): string {
  if (val === null || val === undefined || val === '') return '-'
  const num = typeof val === 'string' ? parseFloat(val) : Number(val)
  if (isNaN(num)) return '-'
  const fixed = num.toFixed(maxDecimals)
  return fixed.replace(/(\.[0-9]*[1-9])0+$|\.0+$/, '$1')
}

/**
 * Converts a monetary amount from a channel's preferred currency to a target currency.
 * Conversion logic:
 * 1. Convert from source currency to TL: valTL = val * rates[fromCurr]
 * 2. Convert from TL to target currency: valTarget = valTL / rates[toCurr]
 */
export function convertCurrency(
  value: number | null | undefined,
  fromCurr: string = 'TL',
  toCurr: string = 'TL',
  rates?: Record<string, number> | null
): number | null {
  if (value === null || value === undefined || isNaN(value)) return null
  if (fromCurr === toCurr) return value
  if (!rates) return value

  const fromRate = rates[fromCurr] ?? (fromCurr === 'TL' ? 1.0 : null)
  const toRate = rates[toCurr] ?? (toCurr === 'TL' ? 1.0 : null)

  if (!fromRate || !toRate || toRate <= 0) return null

  const valueInTL = value * fromRate
  return valueInTL / toRate
}

/**
 * Formats a currency amount with its symbol and Turkish locale number formatting.
 */
export function formatCurrencyAmount(
  value: number | null | undefined,
  currencyCode: string = 'TL',
  currencySymbols: Record<string, string> = { TL: '₺', USD: '$', EUR: '€' }
): string {
  if (value === null || value === undefined || isNaN(value)) return '—'
  const symbol = currencySymbols[currencyCode] || currencyCode
  const formattedNum = Number(value).toLocaleString('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return `${symbol}${formattedNum}`
}

